// @file Whiteboard tools helpers
import { getWhiteboardContentSources, type ContentPickerSource, type ContentSource } from '@@/bits/content_picker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { tailwindFlattenedColors } from '@@/bits/tailwind_config'
import { getEditorObject } from '@@/bits/tldraw/editor'
import type { WhiteboardTool } from '@@/types/whiteboard'
import type { PadletSpeechBubbleShape } from '@padlet/whiteboard-schema'
import type { TLGeoShape, TLGeoShapeGeoStyle } from 'tldraw'

// #region Tool settings
interface WhiteboardToolSetting {
  id: string
  text: string
  ariaLabel: string
  icon: string
}

interface WhiteboardToolSettingEntry extends WhiteboardToolSetting {
  isEnabled: boolean
  isPermitted: boolean
  iconColor?: ContentSource['color']
}

// If you update this, do update the schema in services/rails/app/services/libraries/attachment_type_settings.rb too,
// under the fetch_library_default_attachment_settings_for_whiteboard method.
const TLDRAW_TOOLS_IN_SETTINGS: WhiteboardToolSetting[] = [
  {
    id: 'laser',
    text: __('Pointer'),
    ariaLabel: __('Toggle ‘Pointer’ tool'),
    icon: 'laser_pointer',
  },
  {
    id: 'draw-nested',
    text: __('Draw'),
    ariaLabel: __('Toggle ‘Draw’ tool'),
    icon: 'swiggly_line',
  },
  {
    id: 'text',
    text: __('Text'),
    ariaLabel: __('Toggle ‘Text’ tool'),
    icon: 'text',
  },
  {
    id: 'note',
    text: __('Note'),
    ariaLabel: __('Toggle ‘Note’ tool'),
    icon: 'postnote',
  },
  {
    id: 'geo',
    text: __('Shape'),
    ariaLabel: __('Toggle ‘Shape’ tool'),
    icon: 'geo_star',
  },
  {
    id: 'arrow',
    text: __('Connector'),
    ariaLabel: __('Toggle ‘Connector’ tool'),
    icon: 'solid_single_up_right',
  },
].filter(Boolean) as WhiteboardToolSetting[]

const TLDRAW_TOOL_IDS = TLDRAW_TOOLS_IN_SETTINGS.map((tool) => tool.id)

const PADLET_TOOL_IDS_WITHOUT_PREFIX = getWhiteboardContentSources().map((s) => s.type)

/**
 * Converts a tool ID to the format stored in the settings.
 * Specifically, we prepend `padlet-` to the tool ID if it is a Padlet tool
 * so that there's less of a chance of collision with TLDraw tool IDs.
 */
const toStoredToolId = (toolId: string): string => {
  let storedToolId = toolId
  if (PADLET_TOOL_IDS_WITHOUT_PREFIX.includes(toolId as ContentPickerSource)) {
    storedToolId = `padlet-${toolId}`
  }
  return storedToolId
}

/**
 * Converts a stored tool ID to the original tool ID.
 * Specifically, we remove the `padlet-` prefix if it exists.
 */
const fromStoredToolId = (storedToolId: string): ContentPickerSource | null => {
  const toolId = storedToolId.replace('padlet-', '')
  if (PADLET_TOOL_IDS_WITHOUT_PREFIX.includes(toolId as ContentPickerSource)) {
    return toolId as ContentPickerSource
  }

  return null
}

const PADLET_TOOL_IDS = PADLET_TOOL_IDS_WITHOUT_PREFIX.map((id) => toStoredToolId(id))

const SPECIAL_EFFECTS_TOOL_ID = 'padlet-special-effects'

/**
 * These tools are shown in the toolbar so we must map them from `getWhiteboardContentSources`
 * which already filters out unavailable tools (e.g. if the device doesn't support
 * screen recording).
 */
const AVAILABLE_PADLET_TOOLS: WhiteboardTool[] = getWhiteboardContentSources().map((s) => ({
  id: toStoredToolId(s.type),
  label: s.text,
  iconName: s.icon,
  tldrawToolId: '', // Padlet tools don't have tldrawToolId
}))

const isPadletTool = (toolId: string): boolean => {
  return PADLET_TOOL_IDS.includes(toolId)
}

/**
 * Converts a tool ID to a `ContentPickerSource`.
 * Returns `null` if the tool ID is not a Padlet tool.
 */
const toContentSource = (toolId: string): ContentPickerSource | null => {
  if (!isPadletTool(toolId)) return null
  return toolId.replace('padlet-', '') as ContentPickerSource
}
// #endregion

// #region Tool definitions
const CONNECTOR_COLORS = [
  'park-500',
  'oceanic-500',
  'dark-text-100',
  'canary-600',
  'grape-500',
  'light-text-100',
] as const
const OLD_HIGHLIGHTER_COLORS = [
  'neon-pink',
  'neon-orange',
  'neon-yellow',
  'neon-green',
  'neon-blue',
  'grey-200',
] as const

// Except for grey-200, all highlighter colors are in their own Tailwind color block (neon)
const HIGHLIGHTER_COLORS = [
  'grey-200',
  'neon-red',
  'neon-orange',
  'neon-yellow',
  'neon-green',
  'neon-teal',
  'neon-blue',
  'neon-purple',
  'neon-pink',
  'neon-white',
] as const

const OLD_TEXT_COLORS = [
  'scarlet-500',
  'canary-600',
  'park-500',
  'oceanic-500',
  'dark-text-100',
  'light-text-100',
] as const

const SANDBOX_TOOL_COLORS = [
  'dark-text-100',
  'scarlet-500',
  'tangerine-500',
  'canary-600',
  'park-500',
  'teal-500',
  'oceanic-500',
  'grape-500',
  'padlet-pink-500',
  'light-text-100',
] as const

const TEXT_COLORS_TO_RGB_COLORS: Record<
  (typeof OLD_TEXT_COLORS)[number] | (typeof SANDBOX_TOOL_COLORS)[number],
  string
> = {
  'dark-text-100': tailwindFlattenedColors['dark-text-100'],
  'scarlet-500': tailwindFlattenedColors['scarlet-500'],
  'tangerine-500': tailwindFlattenedColors['tangerine-500'],
  'canary-600': tailwindFlattenedColors['canary-600'],
  'park-500': tailwindFlattenedColors['park-500'],
  'teal-500': tailwindFlattenedColors['teal-500'],
  'oceanic-500': tailwindFlattenedColors['oceanic-500'],
  'grape-500': tailwindFlattenedColors['grape-500'],
  'padlet-pink-500': tailwindFlattenedColors['padlet-pink-500'],
  'light-text-100': tailwindFlattenedColors['light-text-100'],
} as const

const TLDRAW_COLORS_TO_TEXT_COLORS = {
  red: 'scarlet-500',
  'light-red': 'canary-600',
  'light-green': 'park-500',
  blue: 'oceanic-500',
  black: 'dark-text-100',
  grey: 'padlet-pink-500',
  white: 'light-text-100',
} as const
const OLD_DRAW_COLORS = [
  'scarlet-500',
  'canary-600',
  'park-500',
  'oceanic-500',
  'dark-text-100',
  'light-text-100',
] as const

const OLD_NOTES_COLORS = ['scarlet-300', 'canary-200', 'park-200', 'oceanic-200', 'grape-200', 'light-ui-300'] as const

const NOTES_COLORS = [
  'grey-400',
  'scarlet-300',
  'tangerine-300',
  'canary-200',
  'park-200',
  'teal-200',
  'oceanic-200',
  'grape-200',
  'padlet-pink-100',
  'light-ui-300',
] as const

const SHAPE_BORDER_COLORS = [
  'dark-text-100', // black
  'scarlet-500', // red
  'tangerine-500', //  orange
  'canary-600', // yellow
  'oceanic-500', // blue
  'teal-500', //  teal
  'park-500', // green
  'grape-500', //  purple
  'light-text-100', // white
] as const

const SHAPE_FILL_COLORS = [
  'scarlet-100', // red
  'tangerine-100', // orange
  'canary-100', // yellow
  'park-100', // green
  'teal-100', // teal
  'oceanic-100', // blue
  'grape-100', // purple
  'grey-200', // black
  'whiteboard-shape-fill-padlet-pink', // grey
  'grey-50', // white
] as const

// Color mapping for the right side
const SHAPE_BORDER_TO_FAKE_FILL_COLOR_MAP: Record<
  (typeof SHAPE_BORDER_COLORS)[number] | (typeof SANDBOX_TOOL_COLORS)[number],
  string
> = {
  'scarlet-500': 'scarlet-100', // red
  'tangerine-500': 'tangerine-100', // orange
  'canary-600': 'canary-100', // yellow
  'park-500': 'park-100', // green
  'teal-500': 'teal-200', // teal
  'oceanic-500': 'oceanic-100', // blue
  'grape-500': 'grape-100', // purple
  'dark-text-100': 'grey-400', // black
  'padlet-pink-500': 'padlet-pink-100', // grey
  'light-text-100': 'grey-200', // white
}

const SHAPE_BORDER_TO_FAKE_BORDER_COLOR_MAP: Partial<Record<(typeof SHAPE_BORDER_COLORS)[number], string>> = {
  'grape-500': 'grape-500', // purple
}

const DEFAULT_COLORS = {
  // neon-yellow
  highlighter: isAppUsing('unifiedSandboxColors') ? HIGHLIGHTER_COLORS[3] : OLD_HIGHLIGHTER_COLORS[2],
  // black
  text: isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS[0] : OLD_TEXT_COLORS[4],
  // black
  draw: isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS[0] : OLD_DRAW_COLORS[4],
  // canary-100
  note: isAppUsing('unifiedSandboxColors') ? NOTES_COLORS[3] : OLD_NOTES_COLORS[1],
  // oceanic-500
  shapeBorder: isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS[6] : SHAPE_BORDER_COLORS[4],
  // oceanic-500
  connector: isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS[6] : CONNECTOR_COLORS[1],
} as const

type ToolTailwindColorType =
  | (typeof HIGHLIGHTER_COLORS)[number]
  | (typeof OLD_HIGHLIGHTER_COLORS)[number]
  | (typeof SANDBOX_TOOL_COLORS)[number]
  | (typeof OLD_DRAW_COLORS)[number]
  | (typeof NOTES_COLORS)[number]
  | (typeof OLD_NOTES_COLORS)[number]
  | (typeof CONNECTOR_COLORS)[number]
  | (typeof SHAPE_BORDER_COLORS)[number]
  | (typeof SHAPE_FILL_COLORS)[number]

type AllToolTailwindColorListType =
  | typeof HIGHLIGHTER_COLORS
  | typeof OLD_HIGHLIGHTER_COLORS
  | typeof SANDBOX_TOOL_COLORS
  | typeof NOTES_COLORS
  | typeof OLD_DRAW_COLORS
  | typeof OLD_NOTES_COLORS
  | typeof CONNECTOR_COLORS
  | typeof SHAPE_BORDER_COLORS
  | typeof SHAPE_FILL_COLORS

type TldrawColorVariantType = 'highlight' | 'solid' | 'note' | 'semi' | 'geo'

// @see https://github.com/tldraw/tldraw/blob/master/packages/tlschema/src/styles/TLColorStyle.ts
type TldrawDefaultColorStyleColor =
  | 'black'
  | 'grey'
  | 'light-violet'
  | 'violet'
  | 'blue'
  | 'light-blue'
  | 'yellow'
  | 'orange'
  | 'green'
  | 'light-green'
  | 'light-red'
  | 'red'
  | 'white'

// Highlighter colors
const HIGHLIGHT_COLOR_MAP: Record<(typeof HIGHLIGHTER_COLORS)[number], TldrawDefaultColorStyleColor> = {
  'grey-200': 'grey',
  'neon-red': 'red',
  'neon-pink': 'light-violet',
  'neon-yellow': 'yellow',
  'neon-orange': 'orange',
  'neon-green': 'light-green',
  'neon-teal': 'blue',
  'neon-blue': 'light-blue',
  'neon-purple': 'violet',
  'neon-white': 'white',
}

// @see https://www.figma.com/design/qLaHDvXNTsAlfBk2x0IrEf/Unify-Colors-on-Sandbox?node-id=73-7732&t=MocblK3WYDh1ZFCn-4
// Our colors (referenced in the keys) are mapped to TLDraw's default colors (the values in this Record), but we show them to the user via Tailwind.
// This is why "teal" maps to "light-violet", because light-violet is a TLDraw color. Users will see teal-500 though when they go to the sandbox.
// Another example would be padlet-pink-500.
const SOLID_COLOR_MAP: Record<(typeof SANDBOX_TOOL_COLORS)[number], TldrawDefaultColorStyleColor> = {
  'dark-text-100': 'black',
  'scarlet-500': 'red',
  'tangerine-500': 'orange',
  'canary-600': 'light-red', // Discrepancy between this and GEO_AND_CONNECTORS_COLOR_MAP
  'park-500': 'light-green',
  'grape-500': 'violet',
  'teal-500': 'light-violet',
  'oceanic-500': 'blue', // Discrepancy between this and GEO_AND_CONNECTORS_COLOR_MAP
  'padlet-pink-500': 'grey',
  'light-text-100': 'white',
}

// @see https://www.figma.com/design/qLaHDvXNTsAlfBk2x0IrEf/Unify-Colors-on-Sandbox?node-id=73-7732&t=Aie9RnEeEAWRRkNt-0
// On this having a separate map: "yellow/canary-500" is represented as "light-red" in text/draw, and "yellow/canary-600" in shapes/connectors. If we want to keep colors consistent as per , then both Tldraw's light-red and yellow should be mapped to canary-600.
// However, the reverse mapping from canary-600 to yellow and light red does not work because it's one key that represents 2 values.
const GEO_AND_CONNECTORS_COLOR_MAP: Record<(typeof SANDBOX_TOOL_COLORS)[number], TldrawDefaultColorStyleColor> = {
  'dark-text-100': 'black',
  'scarlet-500': 'red',
  'tangerine-500': 'orange',
  'canary-600': 'yellow', // Discrepancy between this and SOLID_COLOR_MAP
  'park-500': 'light-green',
  'grape-500': 'violet',
  'teal-500': 'light-violet',
  'oceanic-500': 'light-blue', // Discrepancy between this and SOLID_COLOR_MAP
  'padlet-pink-500': 'grey',
  'light-text-100': 'white',
}

// @see https://www.figma.com/design/qLaHDvXNTsAlfBk2x0IrEf/Unify-Colors-on-Sandbox?node-id=73-8374&t=MocblK3WYDh1ZFCn-4
// Our colors (referenced in the keys) are mapped to TLDraw's default colors (the values in this Record), but we show them to the user via Tailwind.
// This is why "grey-400" maps to "blue", because blue is a TLDraw color. Users will see grey-400 though when they go to the sandbox.
const NOTE_COLOR_MAP: Record<
  (typeof NOTES_COLORS)[number] | (typeof OLD_NOTES_COLORS)[number],
  TldrawDefaultColorStyleColor
> = {
  'grey-400': 'blue', // Blue is the color that hasn't been used before
  'scarlet-300': 'light-red',
  'tangerine-300': 'orange',
  'canary-200': 'yellow',
  'park-200': 'light-green',
  'oceanic-200': 'light-blue',
  'grape-200': 'violet',
  'teal-200': 'light-violet',
  'padlet-pink-100': 'white',
  'light-ui-300': 'grey',
}

// Shape fill colors
// These all follow the standard Tailwind colors. However, for Padlet Pink, we are creating a new color (whiteboard-shape-fill-padlet-pink) only for Sandbox. Because this new color does not match the existing Padlet Pink 100 set in Tailwind.
const SEMI_COLOR_MAP: Record<(typeof SHAPE_FILL_COLORS)[number], TldrawDefaultColorStyleColor> = {
  'scarlet-100': 'red',
  'tangerine-100': 'orange',
  'canary-100': 'yellow',
  'park-100': 'light-green',
  'teal-100': 'light-violet',
  'oceanic-100': 'light-blue',
  'grape-100': 'violet',
  'grey-200': 'black',
  'whiteboard-shape-fill-padlet-pink': 'grey',
  'grey-50': 'white',
}

const TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR: Record<
  TldrawColorVariantType,
  Partial<Record<ToolTailwindColorType, TldrawDefaultColorStyleColor>>
> = {
  highlight: HIGHLIGHT_COLOR_MAP,
  solid: SOLID_COLOR_MAP,
  geo: GEO_AND_CONNECTORS_COLOR_MAP,
  note: NOTE_COLOR_MAP,
  semi: SEMI_COLOR_MAP,
}

// Only the cone currently in use
type TldrawGeoShapeGeoStyle =
  | 'ellipse'
  | 'rectangle'
  | 'cloud'
  | 'oval'
  | 'arrow-right'
  | 'star'
  | 'heart'
  | 'triangle'
  | 'diamond'
  | 'rhombus'
  | 'hexagon'

/**
 * Currently, tldraw does not support hex colors, so we need to map our colors to the closest tldraw default color style color
 * This is a temporary solution until we patch tldraw to support hex colors or tldraw adds support for custom colors
 **/
function closestTldrawDefaultColorStyleColor(
  toolId: string,
  toolColor: ToolTailwindColorType,
): TldrawDefaultColorStyleColor {
  let colorCategory: TldrawColorVariantType = 'solid'
  switch (toolId) {
    case 'highlight':
      colorCategory = 'highlight'
      break
    case 'geo':
    case 'connector':
      colorCategory = 'geo'
      break
    case 'text':
    case 'draw':
      colorCategory = 'solid'
      break
    case 'note':
      colorCategory = 'note'
      break
    default:
      break
  }
  const nonTldrawColors = ['default', 'dark-text-100']
  if (nonTldrawColors.includes(toolColor)) {
    if (toolId === 'note') {
      return 'grey'
    }
    return 'black'
  }

  return TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR[colorCategory][toolColor] as TldrawDefaultColorStyleColor
}

function createSecondaryMenuFromColors(toolId: string, colors: AllToolTailwindColorListType): WhiteboardTool[] {
  return colors.map((color: ToolTailwindColorType) => ({
    label: '', // TODO: Add color name for accessibility label
    iconName: 'color',
    id: color,
    // tldrawToolId: tailwindFlattenedColors[color], // TODO: Patch tldraw to support hex colors
    tldrawToolId: closestTldrawDefaultColorStyleColor(toolId, color),
  }))
}

const geoToolColors: WhiteboardTool[] = (
  isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS : SHAPE_BORDER_COLORS
).map((color) => ({
  label: '', // TODO: Add color name for accessibility label
  iconName: 'color',
  id: color,
  tldrawToolId: closestTldrawDefaultColorStyleColor('geo', color),
}))

const arrowToolColors: WhiteboardTool[] = createSecondaryMenuFromColors(
  'arrow',
  isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS : CONNECTOR_COLORS,
)

const DRAW_TOOL: WhiteboardTool = {
  label: __('Draw'),
  shortcut: 'D',
  iconName: 'pen',
  id: 'draw-1',
  tldrawToolId: 'draw',
  secondaryMenu: {
    id: 'color',
    items: createSecondaryMenuFromColors(
      'draw',
      isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS : OLD_DRAW_COLORS,
    ),
    defaultSelection: DEFAULT_COLORS.draw,
  },
}

const HIGHLIGHT_TOOL: WhiteboardTool = {
  label: __('Highlight'),
  shortcut: 'H',
  iconName: 'highlighter',
  id: 'highlight',
  tldrawToolId: 'highlight',
  secondaryMenu: {
    id: 'color',
    items: createSecondaryMenuFromColors(
      'highlight',
      isAppUsing('unifiedSandboxColors') ? HIGHLIGHTER_COLORS : OLD_HIGHLIGHTER_COLORS,
    ),
    defaultSelection: DEFAULT_COLORS.highlighter,
  },
}

const ERASER_TOOL: WhiteboardTool = {
  label: __('Eraser'),
  shortcut: 'E',
  iconName: 'eraser',
  id: 'eraser',
  tldrawToolId: 'padlet-draw-eraser',
}

/**
 * All tools available in the whiteboard
 * NOTE: When you add a new primary Tldraw tool here, please also add it to
 * `bits/whiteboard_tools#TLDRAW_TOOLS_IN_SETTINGS` so it can be
 * toggled in the Allowed tools setting panel.
 */
const getAllTools = (): WhiteboardTool[] => [
  {
    label: __('Select'),
    shortcut: 'V',
    iconName: 'cursor',
    id: 'select',
    tldrawToolId: 'select',
  },
  {
    label: __('Interact'),
    shortcut: 'I',
    iconName: 'pointer',
    id: 'interact',
    tldrawToolId: 'interact',
  },
  {
    label: __('Draw'),
    iconName: 'draw-nested',
    id: 'draw-nested',
    tldrawToolId: 'draw-nested',
    secondaryMenu: {
      id: 'draw-tool',
      items: [DRAW_TOOL, HIGHLIGHT_TOOL, ERASER_TOOL],
      defaultSelection: DRAW_TOOL.id,
    },
  },
  {
    label: __('Pointer'),
    shortcut: 'P',
    iconName: 'laser_pointer',
    id: 'laser',
    tldrawToolId: 'laser',
  },
  // Include them here to allow selectedTool to be set to them and for toolStates object
  // they are not in toolSections so they won't be displayed in the toolbar
  { ...DRAW_TOOL, shortcut: '' },
  { ...HIGHLIGHT_TOOL, shortcut: '' },
  { ...ERASER_TOOL, shortcut: '' },

  {
    label: 'Geometry',
    iconName: 'geo',
    id: 'geo',
    tldrawToolId: 'geo',
    secondaryMenu: {
      id: 'geo-shape',
      items: [
        {
          label: __('Ellipse'),
          shortcut: 'O',
          iconName: 'geo_ellipse',
          id: 'ellipse',
          tldrawToolId: 'ellipse',
        },
        {
          label: __('Rectangle'),
          shortcut: 'R',
          iconName: 'geo_rectangle',
          id: 'rectangle',
          tldrawToolId: 'rectangle',
        },
        {
          label: __('Cloud'),
          iconName: 'geo_cloud',
          id: 'cloud',
          tldrawToolId: 'cloud',
        },
        {
          label: __('Oval'),
          iconName: 'geo_oval',
          id: 'oval',
          tldrawToolId: 'oval',
        },
        {
          label: __('Arrow'),
          iconName: 'geo_arrow_right',
          id: 'arrow-right',
          tldrawToolId: 'arrow-right',
        },
        {
          label: __('Star'),
          iconName: 'geo_star',
          id: 'star',
          tldrawToolId: 'star',
        },
        {
          label: __('Heart'),
          iconName: 'geo_heart',
          id: 'heart',
          tldrawToolId: 'heart',
        },

        {
          label: __('Triangle'),
          iconName: 'geo_triangle',
          id: 'triangle',
          tldrawToolId: 'triangle',
        },
        {
          label: __('Diamond'),
          iconName: 'geo_diamond',
          id: 'diamond',
          tldrawToolId: 'diamond',
        },
        {
          label: __('Parallelogram'),
          iconName: 'geo_parallelogram',
          id: 'rhombus',
          tldrawToolId: 'rhombus',
        },
        {
          label: __('Hexagon'),
          iconName: 'geo_hexagon',
          id: 'hexagon',
          tldrawToolId: 'hexagon',
        },
        {
          label: __('Speech Bubble'),
          iconName: 'geo_speech',
          id: 'padlet-speech-bubble',
          tldrawToolId: 'padlet-speech-bubble',
        },
      ],
      defaultSelection: 'rectangle',
    },
  },
  {
    label: __('Note'),
    shortcut: 'N',
    iconName: 'postnote',
    id: 'note',
    tldrawToolId: 'note',
    secondaryMenu: {
      id: 'color',
      items: createSecondaryMenuFromColors(
        'note',
        isAppUsing('unifiedSandboxColors') ? NOTES_COLORS : OLD_NOTES_COLORS,
      ),
      defaultSelection: DEFAULT_COLORS.note,
    },
  },
  {
    label: __('Text'),
    shortcut: 'T',
    iconName: 'text',
    id: 'text',
    tldrawToolId: 'text',
    secondaryMenu: {
      id: 'color',
      items: createSecondaryMenuFromColors(
        'text',
        isAppUsing('unifiedSandboxColors') ? SANDBOX_TOOL_COLORS : OLD_TEXT_COLORS,
      ),
      defaultSelection: DEFAULT_COLORS.text,
    },
  },
  {
    label: __('Connector'),
    shortcut: 'X',
    iconName: 'solid_single_up_right',
    id: 'arrow',
    tldrawToolId: 'arrow',
    secondaryMenu: {
      id: 'connector',
      items: [
        {
          label: 'Double solid',
          iconName: 'solid_double',
          id: 'arrow-draw-arrow',
          tldrawToolId: 'arrow-draw-arrow',
        },
        {
          label: 'Single solid',
          iconName: 'solid_single',
          id: 'none-draw-arrow',
          tldrawToolId: 'none-draw-arrow',
        },
        {
          label: 'None solid',
          iconName: 'solid_none',
          id: 'none-draw-none',
          tldrawToolId: 'none-draw-none',
        },
        {
          label: 'Double dashed',
          iconName: 'dash_double',
          id: 'arrow-dashed-arrow',
          tldrawToolId: 'arrow-dashed-arrow',
        },
        {
          label: 'Single dashed',
          iconName: 'dash_single',
          id: 'none-dashed-arrow',
          tldrawToolId: 'none-dashed-arrow',
        },
        {
          label: 'None dashed',
          iconName: 'dash_none',
          id: 'none-dashed-none',
          tldrawToolId: 'none-dashed-none',
        },
      ],
      defaultSelection: 'none-draw-arrow',
    },
  },
  {
    label: isAppUsing('sandboxToolbarV11') ? __('More tools') : __('Attachment'),
    iconName: isAppUsing('sandboxToolbarV11') ? 'more_rounded' : 'more_attachments',
    id: 'padlet-attachment',
    tldrawToolId: 'padlet-attachment',
  },
  {
    label: __('Media search'),
    iconName: 'media',
    id: 'padlet-media_search',
    tldrawToolId: 'padlet-media_search',
  },
  {
    label: __('Undo'),
    shortcut: '',
    iconName: 'undo',
    id: 'undo',
    tldrawToolId: 'undo',
  },
  {
    label: __('Redo'),
    shortcut: '',
    iconName: 'redo',
    id: 'redo',
    tldrawToolId: 'redo',
  },
  ...AVAILABLE_PADLET_TOOLS,
]

const getAllToolsAndSecondaryTools = (): WhiteboardTool[] => {
  const allTools = getAllTools()
  return allTools.concat(
    allTools.filter((tool) => tool.secondaryMenu != null).flatMap((tool) => tool.secondaryMenu?.items ?? []),
  )
}

function getTldrawToolId(toolId: string): string | undefined {
  return getAllToolsAndSecondaryTools().find((tool) => tool.id === toolId)?.tldrawToolId
}

// Used to display menu of GeoStyles that can be applied to a GeoShape element
const geoShapeGeoStyles: WhiteboardTool[] =
  getAllTools()
    .filter((tool) => tool.id === 'geo')[0]
    .secondaryMenu?.items.map((item) => ({
      label: item.label,
      /* Unlike the toolbar, the element modifier uses expanded versions of the geo shape icons. 
    The only expansion is the square icon, which isn't expanded fully so that it can fit nicely within the 28px circle background of buttons 
    We might eventually want to use these expanded icons for the toolbar as well 
    */
      iconName: item.iconName + '_expanded',
      id: item.id,
      tldrawToolId: item.tldrawToolId,
    })) ?? []
function getGeoShapeGeoStyles(): WhiteboardTool[] {
  const padletSpeechBubbleTool = getAllTools().find((tool) => tool.id === 'padlet-speech-bubble')
  const menuItems = [
    ...(getAllTools().filter((tool) => tool.id === 'geo')[0].secondaryMenu?.items ?? []),
    ...(padletSpeechBubbleTool != null ? [padletSpeechBubbleTool] : []),
  ]
  return (
    menuItems.map((item) => ({
      label: item.label,
      /* Unlike the toolbar, the element modifier uses expanded versions of the geo shape icons. 
        The only expansion is the square icon, which isn't expanded fully so that it can fit nicely within the 28px circle background of buttons 
        We might eventually want to use these expanded icons for the toolbar as well 
        */
      iconName: item.iconName + '_expanded',
      id: item.id,
      tldrawToolId: item.tldrawToolId,
    })) ?? []
  )
}
// #endregion

function createPadletSpeechBubbleShapeFromGeoShape(geoShape: TLGeoShape): void {
  const tldrawEditor = getEditorObject()
  tldrawEditor?.createShape({
    type: 'padlet-speech-bubble',
    index: geoShape.index,
    x: geoShape.x,
    y: geoShape.y,
    opacity: geoShape.opacity,
    rotation: geoShape.rotation,
    isLocked: geoShape.isLocked,
    props: {
      w: geoShape.props.w,
      h: geoShape.props.h,
      color: geoShape.props.color,
      size: geoShape.props.size,
      font: geoShape.props.font,
      align: geoShape.props.align,
      verticalAlign: geoShape.props.verticalAlign,
      growY: geoShape.props.growY,
      richText: geoShape.props.richText,
      scale: geoShape.props.scale,
      url: geoShape.props.url,
      fill: geoShape.props.fill,
      dash: geoShape.props.dash,
      tail: { x: 0.5, y: 1.5 },
    },
    meta: {
      ...geoShape.meta,
      updatingShapeId: geoShape.id,
    },
  })
}

function createGeoShapeFromPadletSpeechBubbleShape({
  padletSpeechBubbleShape,
  geoStyle,
}: {
  padletSpeechBubbleShape: PadletSpeechBubbleShape
  geoStyle: TLGeoShapeGeoStyle
}): void {
  const tldrawEditor = getEditorObject()
  tldrawEditor?.createShape({
    type: 'geo',
    index: padletSpeechBubbleShape.index,
    x: padletSpeechBubbleShape.x,
    y: padletSpeechBubbleShape.y,
    opacity: padletSpeechBubbleShape.opacity,
    rotation: padletSpeechBubbleShape.rotation,
    isLocked: padletSpeechBubbleShape.isLocked,
    props: {
      w: padletSpeechBubbleShape.props.w,
      h: padletSpeechBubbleShape.props.h,
      color: padletSpeechBubbleShape.props.color,
      size: padletSpeechBubbleShape.props.size,
      font: padletSpeechBubbleShape.props.font,
      align: padletSpeechBubbleShape.props.align,
      verticalAlign: padletSpeechBubbleShape.props.verticalAlign,
      growY: padletSpeechBubbleShape.props.growY,
      richText: padletSpeechBubbleShape.props.richText,
      scale: padletSpeechBubbleShape.props.scale,
      url: padletSpeechBubbleShape.props.url,
      fill: padletSpeechBubbleShape.props.fill,
      dash: padletSpeechBubbleShape.props.dash,
      geo: geoStyle,
    },
    meta: {
      ...padletSpeechBubbleShape.meta,
      updatingShapeId: padletSpeechBubbleShape.id,
    },
  })
}

// #endregion
export {
  arrowToolColors,
  AVAILABLE_PADLET_TOOLS,
  CONNECTOR_COLORS,
  createGeoShapeFromPadletSpeechBubbleShape,
  createPadletSpeechBubbleShapeFromGeoShape,
  DEFAULT_COLORS,
  fromStoredToolId,
  geoShapeGeoStyles,
  geoToolColors,
  getAllTools,
  getGeoShapeGeoStyles,
  getTldrawToolId,
  isPadletTool,
  PADLET_TOOL_IDS,
  PADLET_TOOL_IDS_WITHOUT_PREFIX,
  SHAPE_BORDER_TO_FAKE_BORDER_COLOR_MAP,
  SHAPE_BORDER_TO_FAKE_FILL_COLOR_MAP,
  SPECIAL_EFFECTS_TOOL_ID,
  TEXT_COLORS_TO_RGB_COLORS,
  TLDRAW_COLORS_TO_TEXT_COLORS,
  TLDRAW_TOOL_IDS,
  TLDRAW_TOOLS_IN_SETTINGS,
  toContentSource,
  TOOL_COLOR_TO_TLDRAW_DEFAULT_COLOR_STYLE_COLOR,
  toStoredToolId,
}
export type {
  TldrawColorVariantType,
  TldrawDefaultColorStyleColor,
  TldrawGeoShapeGeoStyle,
  ToolTailwindColorType,
  WhiteboardToolSettingEntry,
}
